import PropTypes from 'prop-types'
import R from 'ramda'
import styled from 'styled-components'
import { Link as RouteLink } from 'react-router-dom'
import { useQueryParam, NumberParam } from 'use-query-params'
import { useState } from 'react'

import {
  Box,
  Button,
  Columns,
  Col,
  Dialog,
  Icon,
  Heading,
  Image,
  Link,
  SimpleAudio,
  Text,
  TransparentButton,
  media,
  theme,
} from '@redriverpress/system'

import { useTextToSpeech } from 'Packages/tts'

import AccessDialog from './AccessDialog'
import SaveToSetsDialog from '../../../packages/flashcards/save/SaveToSetsDialog'
import flashcardPropShape from '../constants/flashcardPropShape'
import useSelection from '../hooks/useSelection'

const Content = styled(Dialog.Content)`
  position: fixed;
  inset: 0;
  overflow-y: scroll;
  background: ${theme.colors.gray100};
  -webkit-overflow-scrolling: touch;
  z-index: 6;

  ${media.md`
    inset: unset;
    top: 0;
    left: 50%;
    transform: translateX(-50%);
    margin-top: 2rem;
    width: 60vw;
    max-width: 25rem;
    border-radius: ${props => props.theme.radii.rounded};
  `}

  ${media.lg`
    padding: 1rem;
    width: 75vw;
    max-width: 60rem;
  `}
`

const SelectButton = styled.button`
  appearance: none;
  background: ${props =>
    props.theme.colors[props.isSelected ? 'blue500' : 'gray150']};
  border-radius: ${props => props.theme.radii.circular};
  color: ${props => props.theme.colors[props.isSelected ? 'white' : 'black']};
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  height: 40px;
  width: 40px;
  padding: 0; // reset browser defaults
  position: absolute;
  bottom: ${props => props.theme.space[2]};

  &:hover,
  &:focus-visible {
    background: ${props =>
      props.theme.colors[props.isSelected ? 'blue700' : 'blue500']};
    color: ${props => props.theme.colors.white};
  }

  &:focus {
    outline: none;
  }

  &:focus-visible {
    box-shadow: 0 0 0 2px ${props =>
      props.theme.colors.white}, 0 0 0 4px ${props =>
  props.theme.colors.blue500};
  }

  &:after {
    font-family: 'Font Awesome 5 Pro';
    font-weight: 300;
    color: ${props => props.theme.colors[props.isSelected ? 'white' : 'black']};
    content: '${props => (props.isSelected ? '\f00c' : '\f067')}';
    display: block;
    z-index: 1;
  }

  &:hover:after,
  &:focus-visible:after {
    color: ${props => props.theme.colors.white};
  }
`

const FCButton = styled(TransparentButton)`
  border-radius: ${props => props.theme.radii.rounded};
  color: ${props => props.theme.colors.black};

  &:focus {
    outline: none;
  }

  &:focus-visible {
    box-shadow: 0 0 0 2px ${props => props.theme.colors.blue500};
  }

  &:after {
    content: '';
    position: absolute;
    inset: 0;
    border-radius: ${props => props.theme.radii.rounded};
    box-shadow: ${props => props.theme.shadows[3]};
    opacity: 0;
    transition: 0.15s ease-in;
  }

  &:hover:after,
  &:focus:after {
    opacity: 1;
  }
`

export default function Flashcard({ flashcard, showCategory, userHasAccess }) {
  const { selection, addToSelection, removeFromSelection } = useSelection()

  const isSelected = R.any(R.propEq('id', flashcard.id), selection)

  const [flashcardParam, setFlashcardParam] = useQueryParam(
    'flashcard',
    NumberParam
  )

  const dialogIsOpen = flashcardParam === flashcard.id

  // Don't fire the TTS request until the details modal has been opened
  const { data: ttsData } = useTextToSpeech(flashcard.title, {
    enabled: dialogIsOpen,
  })

  const [accessDialogOpen, setAccessDialogOpen] = useState(false)
  const [saveDialogOpen, setSaveDialogOpen] = useState(false)

  const handleSelectButton = () =>
    isSelected
      ? removeFromSelection([flashcard.id])
      : addToSelection([flashcard])

  return (
    <Dialog.Root
      open={dialogIsOpen}
      onOpenChange={open => setFlashcardParam(open ? flashcard.id : undefined)}
    >
      <Box
        bg='white'
        borderRadius='rounded'
        position='relative'
        display='flex'
        justifyContent='center'
      >
        <Dialog.Trigger asChild>
          <FCButton
            pt={2}
            px={3}
            pb={5}
            width={1}
            aria-label={`${flashcard.title} flashcard, view more information`}
          >
            <Image
              src={flashcard.thumbnail}
              alt=''
              size={[100, null, 80, 100]}
              mb={2}
            />
            <Heading as='h6' mb={0}>
              {flashcard.title}
            </Heading>
            {showCategory && (
              <Text fontSize={0}>{flashcard.category.title}</Text>
            )}
          </FCButton>
        </Dialog.Trigger>
        <SelectButton
          aria-label={
            isSelected
              ? `Remove ${flashcard.title} flashcard from selection`
              : `Add ${flashcard.title} flashcard to selection`
          }
          isSelected={isSelected}
          onClick={() =>
            userHasAccess ? handleSelectButton() : setAccessDialogOpen(true)
          }
        />
      </Box>

      {!userHasAccess && (
        <AccessDialog
          open={accessDialogOpen}
          onOpenChange={open => setAccessDialogOpen(open)}
        />
      )}

      <Dialog.Portal>
        <Dialog.Overlay />

        <Content>
          <Dialog.Close position='absolute' top={2} right={2} />
          <Columns alignItems={userHasAccess ? null : 'center'}>
            <Col width={[1, null, null, userHasAccess ? 1 / 2 : 'auto']}>
              <Box
                p={[4, null, null, userHasAccess ? 0 : 4]}
                mx={[null, null, null, userHasAccess ? null : 4]}
                bg={[
                  'white',
                  null,
                  null,
                  userHasAccess ? 'transparent' : 'white',
                ]}
                borderRadius={[null, null, null, 'rounded']}
              >
                <Image
                  src={userHasAccess ? flashcard.image : flashcard.thumbnail}
                  display='block'
                  borderRadius={[null, null, null, 'rounded']}
                  width={[1, null, null, userHasAccess ? 1 : 100]}
                  height='auto'
                  alt={flashcard.title}
                />
              </Box>
            </Col>
            <Col width={[1, null, null, 1 / 2]}>
              <Box
                p={[3, null, null, 0]}
                display={['block', null, null, 'flex']}
                flexDirection={[null, null, null, 'column']}
                height={[null, null, null, '100%']}
              >
                <Text
                  fontSize={0}
                  pb={[3, null, null, 4]}
                  pr={[null, null, null, 5]} // pr clears Dialog.Close
                >
                  <Text as='span' display='inline-block'>
                    <RouteLink to={`/flashcards/${flashcard.genre.slug}`}>
                      {flashcard.genre.title}
                    </RouteLink>
                    <Icon icon='angle-right' px={2} />
                  </Text>
                  <Text as='span' display='inline-block'>
                    <RouteLink
                      to={`/flashcards/${flashcard.genre.slug}/${flashcard.category.slug}`}
                    >
                      {flashcard.category.title}
                    </RouteLink>
                  </Text>
                </Text>
                <Box display='flex' pb={[4, null, null, 5]}>
                  <Box mr={3}>
                    <SimpleAudio src={ttsData?.audio_url} small />
                  </Box>
                  <Dialog.Title asChild>
                    <Heading as='h2' mb={0} flex={1}>
                      {flashcard.title}
                    </Heading>
                  </Dialog.Title>
                </Box>
                {userHasAccess ? (
                  <Box mt={[null, null, null, 'auto']}>
                    <Button
                      width={[1, null, null, 'auto']}
                      mb={[2, null, null, 0]}
                      mr={[null, null, null, 2]}
                      onClick={handleSelectButton}
                    >
                      <Icon icon={isSelected ? 'minus' : 'plus'} pr={2} />
                      {isSelected ? 'Deselect' : 'Select'}
                    </Button>
                    <Button
                      secondary
                      width={[1, null, null, 'auto']}
                      onClick={() => setSaveDialogOpen(true)}
                    >
                      <Icon icon='image' pr={2} />
                      Save to Set
                    </Button>
                  </Box>
                ) : (
                  <>
                    <Text>
                      To save, customize, print, and share flashcards, please
                      purchase a&nbsp;subscription.
                    </Text>
                    <Box my={4}>
                      <Button small secondary mr={2} as='a' href='/sign_in'>
                        <Icon icon='sign-in' pr={2} />
                        Log In
                      </Button>
                      <Button small as='a' href='/pricing'>
                        <Icon icon='plus' pr={2} />
                        Sign Up
                      </Button>
                    </Box>
                    <Box mb={3}>
                      <Link
                        fontWeight={600}
                        color='black'
                        href='/tour/flashcards'
                      >
                        Learn about Flashcards
                      </Link>
                    </Box>
                  </>
                )}
              </Box>
            </Col>
          </Columns>
          {userHasAccess && (
            <SaveToSetsDialog
              flashcards={[
                {
                  id: flashcard.id,
                  title: flashcard.title,
                  userTitle: flashcard.title,
                },
              ]}
              open={saveDialogOpen}
              onClose={() => setSaveDialogOpen(false)}
              onOpenChange={open => setSaveDialogOpen(open)}
            />
          )}
        </Content>
      </Dialog.Portal>
    </Dialog.Root>
  )
}

Flashcard.defaultProps = {
  showCategory: false,
  userHasAccess: false,
}

Flashcard.propTypes = {
  showCategory: PropTypes.bool,
  flashcard: PropTypes.shape(flashcardPropShape).isRequired,
  userHasAccess: PropTypes.bool,
}
